import type {
	I2VAspectRatio,
	I2VModelId,
	I2VResolution,
} from "@saas/image-to-video/types";

/**
 * /ai-dance-video-generator 的预设：一张照片 + 一支有名字的舞（或用户自己传的舞蹈视频）做动作迁移（2026-11-01）。
 * 方案见 docs/research/ai-dance-video-research-2026-11-01.md。
 * 这里只放 type import，apps/web/test 的 transpile loader 才能直接加载。
 */
export const AI_DANCE_VIDEO_PAGE_PATH = "/ai-dance-video-generator";

/** 登录前现场的 IndexedDB key，和 /image-to-video、首页、其他预设页分开 */
export const AI_DANCE_VIDEO_PENDING_STATE_KEY = "pendingAiDanceVideoState";

export type AiDanceVideoQualityId = "lite" | "standard" | "hd";

/**
 * Lite / Standard 是 Seedance 2.0 Mini 参考模式；HD 是 Kling 2.6 motion-control（第 3 步动作和竞品的 Kling 3.0 几乎逐帧一样）。
 * Mini 按（参考秒 + 生成秒）计：Lite 480p 2.4 × (2 + 4) = 14，Standard 480p 2.4 × (8 + 8) = 38；
 * HD 只按参考秒计：11 × 8 = 88（成片和参考片一样长，时长这里只是占位）
 */
export const AI_DANCE_VIDEO_QUALITIES: {
	id: AiDanceVideoQualityId;
	model: I2VModelId;
	resolution: I2VResolution;
	duration: number;
}[] = [
	{ id: "lite", model: "seedance-2-mini", resolution: "480p", duration: 4 },
	{
		id: "standard",
		model: "seedance-2-mini",
		resolution: "480p",
		duration: 8,
	},
	{ id: "hd", model: "kling-2.6-motion", resolution: "720p", duration: 8 },
];

export const AI_DANCE_VIDEO_PRESET = {
	mode: "reference",
	defaultQualityId: "lite" as AiDanceVideoQualityId,
	/** 舞蹈片都是竖的，三档都只出 9:16 */
	aspectRatioOptions: ["9:16"] as I2VAspectRatio[],
	defaultAspectRatio: "9:16" as I2VAspectRatio,
	/** 每档时长都固定，这里只给步骤示意图画分段用 */
	durationOptions: [4, 8],
	defaultDuration: 8,
	slotCount: 1,
} as const;

/** 舞蹈库：参考片放在用户上传那个桶 images-art-prod（后端只收 NEXT_PUBLIC_R2_PUBLIC_URL 下的参考视频） */
export const AI_DANCE_VIDEO_MOTION_SECONDS = 8;
/** Lite 只挂参考片 1–3s，出 4 秒，正好进免费额度 */
export const AI_DANCE_VIDEO_MOTION_LITE_SECONDS = 2;
export const AI_DANCE_VIDEO_MOTION_ASPECT_RATIO: I2VAspectRatio = "9:16";

/** 用户自己传的舞蹈视频最长 10 秒：Kling motion-control 按照片朝向时的上限，超长截前 10 秒 */
export const AI_DANCE_VIDEO_USER_VIDEO_MAX_SECONDS = 10;
/** Lite 只截 2 秒没意义，用户视频只开 Standard / HD */
export const AI_DANCE_VIDEO_USER_VIDEO_QUALITIES: AiDanceVideoQualityId[] = [
	"standard",
	"hd",
];

export type AiDanceVideoDanceId =
	| "hiphop"
	| "funky"
	| "party"
	| "disco"
	| "street"
	| "bounce";
export type AiDanceVideoTemplateId = AiDanceVideoDanceId | "own";

/**
 * 每支舞参考片的版本。v1 是 Pexels 静音片 + 另配的 Suno 音乐；v2（11/01 老公看过）换成 Seedance 生成、
 * 自带同一条配乐的参考舞，舞是跟着这段音乐跳的，所以踩拍。Funky、Street 原来的舞不像舞，先换这两支；
 * 当晚 Party、Disco、Bounce、Hip-Hop 也换了，6 支全是 v2
 */
const AI_DANCE_MOTION_VERSION: Record<AiDanceVideoDanceId, number> = {
	hiphop: 2,
	funky: 2,
	party: 2,
	disco: 2,
	street: 2,
	bounce: 2,
};

/** 服务端按这些 key 出片后换成配乐（preset-audio.ts） */
export const aiDanceMotionPath = (id: AiDanceVideoDanceId, lite = false) =>
	`presets/ai-dance/motion-${id}${lite ? "-lite" : ""}-v${AI_DANCE_MOTION_VERSION[id]}.mp4`;

/** 同其他特效页：用户只能加一句 director's note，拼到模板后面 */
export const AI_DANCE_VIDEO_NOTE_PREFIX = "\n\nDirector's note: ";

/** 只看 note 是否全空白，不 trim 内容本身：工作台每敲一个字都会走这里 */
export function buildAiDanceVideoPrompt(
	templatePrompt: string,
	note: string,
): string {
	return note.trim()
		? `${templatePrompt}${AI_DANCE_VIDEO_NOTE_PREFIX}${note}`
		: templatePrompt;
}

/** 舞名进 prompt：工作台靠 prompt 反推当前模板，每支舞的写法必须不同 */
const DANCES: { id: AiDanceVideoDanceId; name: string }[] = [
	{ id: "hiphop", name: "hip-hop" },
	{ id: "funky", name: "funky groove" },
	{ id: "party", name: "party groove" },
	{ id: "disco", name: "disco" },
	{ id: "street", name: "street freestyle" },
	{ id: "bounce", name: "bounce" },
];

const KEEP_PHOTO =
	"Keep the background, lighting, camera angle and framing of @Image 1; the camera stays fixed. Full body in frame.";

const RULES =
	"Natural anatomy: no extra limbs, no deformed hands, no warped face, no flicker. No dialogue, no text, no watermark.";

/** Standard：第 3 步 Mini 那条（prompt-mini-v1.txt）的写法，加上舞名 */
function miniPrompt(dance: string) {
	return `The person in @Image 1 performs the exact ${dance} from @Video 1. @Video 1 is only the source of the dance moves: copy every body, arm, hand, hip, leg, foot and head movement, the timing and rhythm 1:1, with no new or missing moves. Never show the dancer from @Video 1: from the very first frame the performer is the person in @Image 1, with the same face, hairstyle, hair color, skin tone, body shape, clothes and accessories as in the photo, sharp and recognizable in every frame. ${KEEP_PHOTO} ${RULES}`;
}

/** Lite：只挂 2 秒参考，复制完照同一个风格和节奏跳到 4 秒（同 Hop Hop Hop Lite 的做法） */
function litePrompt(dance: string) {
	return `The person in @Image 1 performs the ${dance} from @Video 1. Copy the moves of @Video 1 1:1 for the first 2 seconds, then keep dancing in the same style and rhythm until the end, with no pause and no new shot. Never show the dancer from @Video 1: from the very first frame the performer is the person in @Image 1, with the same face, hairstyle, hair color, skin tone, body shape, clothes and accessories as in the photo, sharp and recognizable in every frame. ${KEEP_PHOTO} ${RULES}`;
}

/** HD：Kling 2.6 motion-control，改写自 easemate 的模板骨架（第 3 步那条 2450 字，这里去掉重复、改成不分性别） */
function klingPrompt(dance: string) {
	return `Use the person from the uploaded image as the performer and transfer the ${dance} from the reference video with exact 1:1 motion mapping.

Every movement must match the reference video: body posture, arm and hand movements, shoulders, hips, legs, foot placement, head movements, weight shifts, body rotations, timing, rhythm, pauses and transitions. Do not simplify, reinterpret or add any new movements.

Preserve the identity of the person from the uploaded image exactly: face, facial proportions, hairstyle, hair color, skin tone, body shape, clothing and accessories stay unchanged in every frame, with no facial drift or flicker. The face stays sharp and recognizable from beginning to end.

Only transfer the dance motion. Keep the background, environment, lighting, colors, camera angle, framing and camera distance exactly as in the uploaded image.

Natural human anatomy with stable proportions: no limb stretching, no duplicated limbs, no missing fingers, no hand deformation. Smooth, realistic, photorealistic motion.`;
}

/**
 * 6 支舞 + 用户自己传（own）。舞的参考片三档共用 8 秒那段，Lite 换 2 秒那段（工作台里拼完整地址）。
 * 出片后服务端统一换成配乐，用户视频不换。test/ai-dance-video-preset.test.mjs 卡住长度和这几条
 */
export const AI_DANCE_VIDEO_TEMPLATES: {
	id: AiDanceVideoTemplateId;
	prompt: string;
	promptByQuality: Partial<Record<AiDanceVideoQualityId, string>>;
	userVideo?: true;
}[] = [
	...DANCES.map(({ id, name }) => ({
		id,
		prompt: miniPrompt(`${name} dance`),
		promptByQuality: {
			lite: litePrompt(`${name} dance`),
			hd: klingPrompt(`${name} dance`),
		},
	})),
	{
		id: "own",
		prompt: miniPrompt("dance"),
		promptByQuality: { hd: klingPrompt("dance") },
		userVideo: true,
	},
];
